<template>
  <div>
    <!-- Brand-new team with zero leads ever: replace the normal page with a
         teaching empty state that centers the interest-form URL and the
         "Add lead" action. -->
    <prospect-interest-form-card
      v-if="summaryLoaded && isBrandNew"
      :inquiry-url="inquiryUrl"
      variant="hero"
    />

    <template v-else>
      <!-- Top row: stats on the left, compact interest-form card on the right.
           Stacks on mobile. -->
      <div v-if="summaryLoaded" class="prospect-page-header">
        <prospect-stats-strip
          :summary="summary"
          :conversion-range="conversionRange"
          :conversion-start="conversionStartDate"
          :conversion-end="conversionEndDate"
          @range-change="onConversionRangeChange"
        />
        <prospect-interest-form-card
          :inquiry-url="inquiryUrl"
          variant="compact"
        />
      </div>

      <!-- Sticky toolbar -->
      <prospect-toolbar
        :view-mode="viewMode"
        :search-query="searchQuery"
        :active-stage="activeStage"
        :assigned-filter="assignedFilter"
        :filter-question-id="filterQuestionId"
        :filter-answer-value="filterAnswerValue"
        :stage-counts="stageCounts"
        :coach-options="coachOptions"
        :filterable-questions="filterableQuestions"
        :filter-answer-options="filterAnswerOptions"
        @update:viewMode="onViewModeChange"
        @update:searchQuery="onSearchInput"
        @update:activeStage="onStageChange"
        @update:assignedFilter="onAssignedFilterChange"
        @update:filterQuestionId="onFilterQuestionChange"
        @update:filterAnswerValue="onFilterAnswerChange"
        @clear-answer-filter="clearAnswerFilter"
      />

      <!-- Loading -->
      <spinner v-if="loading"></spinner>

      <!-- Error -->
      <div v-if="error" class="alert alert-danger">
        {{ errorMessage }}
      </div>

      <!-- Filter banner — surfaces which filters are currently narrowing the
           list and gives a single click to recover. Lists active filters
           inline rather than splitting per-axis counts (the toolbar already
           exposes per-filter clears via dropdowns). -->
      <div v-if="!loading && !error && viewMode !== 'funnel' && showFilterBanner" class="prospect-filter-banner">
        <span>
          <i class="fa-solid fa-filter m-r-xxs" aria-hidden="true"></i>
          <span v-if="hiddenLeadsCount > 0">
            {{ hiddenLeadsCount }}
            {{ hiddenLeadsCount === 1 ? 'lead' : 'leads' }}
            hidden ·
          </span>
          Filtered by {{ activeFilterLabels.join(', ') }}
        </span>
        <button type="button" class="btn btn-link btn-sm" @click="resetFilters">
          Reset filters
        </button>
      </div>

      <!-- Queue -->
      <prospect-queue
        v-if="!loading && !error && viewMode === 'queue'"
        :families="families"
        :upcoming-trials="upcomingTrials"
        :families-api-url="familiesApiUrl"
        :team-name="teamName"
        :current-author-id="currentAuthorId"
        :current-author-type="currentAuthorType"
        :loading="loading"
        :hidden-by-filters="showFilterBanner"
        @open="openDrawer"
        @note-created="onNoteCreated"
        @stage-change="onStageChangePayload"
        @reset-filters="resetFilters"
      />

      <!-- All Leads -->
      <prospect-all-list
        v-if="!loading && !error && viewMode === 'all'"
        :families="families"
        :families-api-url="familiesApiUrl"
        :team-name="teamName"
        :current-author-id="currentAuthorId"
        :current-author-type="currentAuthorType"
        :active-stage="activeStage"
        :loading="loading"
        @open="openDrawer"
        @note-created="onNoteCreated"
        @stage-change="onStageChangePayload"
      />

      <!-- Funnel report -->
      <prospect-funnel-report
        v-if="viewMode === 'funnel'"
        :funnel-url="funnelUrl"
      />
    </template>

    <!-- Drawer -->
    <prospect-drawer
      v-if="selectedFamily"
      :family="selectedFamily"
      :families-api-url="familiesApiUrl"
      :team-name="teamName"
      :team-timezone="teamTimezone"
      :coach-options="coachOptions"
      :current-author-id="currentAuthorId"
      :current-author-type="currentAuthorType"
      :mention-suggest-url="mentionSuggestUrl"
      :access-key="accessKey"
      :auth-token="authToken"
      :cloudfront-base-url="cloudfrontBaseUrl"
      :endpoint="endpoint"
      @close="closeDrawer"
      @stage-change="onStageChangePayload"
      @trial-date-save="onTrialDateSave"
      @trial-class-booked="onTrialClassBooked"
      @follow-up-toggle="onFollowUpToggle"
      @assigned-coach-change="onAssignedCoachChange"
      @family-contact-save="onFamilyContactSave"
      @child-details-save="onChildDetailsSave"
      @note-created="onDrawerNoteCreated"
    />
  </div>
</template>

<script>
import Spinner from './spinner.vue';
import ProspectStatsStrip from './prospect_stats_strip.vue';
import ProspectInterestFormCard from './prospect_interest_form_card.vue';
import ProspectToolbar from './prospect_toolbar.vue';
import ProspectQueue from './prospect_queue.vue';
import ProspectAllList from './prospect_all_list.vue';
import ProspectDrawer from './prospect_drawer.vue';
import ProspectFunnelReport from './prospect_funnel_report.vue';

export default {
  name: 'prospect-list',
  components: {
    Spinner,
    ProspectStatsStrip,
    ProspectInterestFormCard,
    ProspectToolbar,
    ProspectQueue,
    ProspectAllList,
    ProspectDrawer,
    ProspectFunnelReport,
  },
  props: {
    apiUrl: { type: String, required: true },
    familiesApiUrl: { type: String, required: true },
    summaryUrl: { type: String, required: true },
    funnelUrl: { type: String, required: true },
    inquiryUrl: { type: String, required: true },
    teamName: { type: String, default: '' },
    teamTimezone: { type: String, default: '' },
    questionsUrl: { type: String, default: '' },
    coachOptions: { type: Array, default: () => [] },
    currentAuthorId: { type: Number, required: true },
    currentAuthorType: { type: String, required: true },
    mentionSuggestUrl: { type: String, default: '' },
    accessKey: { type: String, default: '' },
    authToken: { type: String, default: '' },
    cloudfrontBaseUrl: { type: String, default: '' },
    endpoint: { type: String, default: '' },
  },
  data() {
    return {
      summary: {},
      summaryLoaded: false,
      conversionRange: '90',
      conversionStartDate: '',
      conversionEndDate: '',

      families: [],
      upcomingTrials: [],
      loading: false,
      error: false,
      errorMessage: '',

      viewMode: localStorage.getItem('prospect_list_view_mode') || 'queue',
      activeStage: '',
      attentionMode: 'needs_attention',
      assignedFilter: localStorage.getItem('prospect_list_assigned_filter') !== null
        ? localStorage.getItem('prospect_list_assigned_filter')
        : 'me',
      prospectQuestions: [],
      filterQuestionId: '',
      filterAnswerValue: '',

      searchQuery: '',
      searchDebounceTimer: null,

      selectedFamilyId: null,
      // Family pulled in via ?family_id= when the lead isn't in the current
      // filtered list (e.g. a coach clicks the email CTA while filtered to
      // "My leads"). Acts as a fallback source for selectedFamily so the
      // drawer can open without mutating the user's filters.
      deepLinkedFamily: null,
      pendingDeepLinkFamilyId: null,
    };
  },
  computed: {
    stageCounts() {
      if (!this.summary) return {};
      if (this.attentionMode === 'needs_attention') return this.summary.by_stage_needs_attention || {};
      return this.summary.by_stage || {};
    },
    expectedCountForCurrentFilter() {
      if (!this.summaryLoaded) return 0;
      if (this.activeStage) return this.stageCounts[this.activeStage] || 0;
      if (this.attentionMode === 'needs_attention') return this.summary.families_needs_attention_count || 0;
      return this.summary.families_total_count || 0;
    },
    searchActive() {
      return this.searchQuery.trim().length > 0;
    },
    // True only for teams that have literally zero leads in any stage, ever.
    // A team with archived/converted leads from last season is not "brand new."
    isBrandNew() {
      return (this.summary.families_total_count || 0) === 0;
    },
    // The banner trigger. Includes search/stage/question filters and the
    // case where a coach picked a specific colleague to view (not their
    // own default 'me' and not the 'all coaches' broaden). Default 'me'
    // alone doesn't trigger — that's just the page's resting state for a
    // logged-in coach and would make the banner always-on.
    hasActiveFilters() {
      const specificCoach = !!this.assignedFilter
        && this.assignedFilter !== 'me';
      return (
        this.searchActive
        || !!this.activeStage
        || (!!this.filterQuestionId && !!this.filterAnswerValue)
        || specificCoach
      );
    },
    // Human-readable list of currently-active filters, shown inline in the
    // banner. We include the coach perspective ('me' / a specific coach
    // name) when it's narrower than "all coaches", since it IS part of
    // what's hiding leads from the current view.
    activeFilterLabels() {
      const labels = [];
      if (this.activeStage) labels.push(this.stageLabel(this.activeStage));
      if (this.searchActive) labels.push(`"${this.searchQuery.trim()}"`);
      if (this.filterQuestionId && this.filterAnswerValue) {
        labels.push('a custom answer');
      }
      if (this.assignedFilter === 'me') {
        labels.push('My leads');
      } else if (this.assignedFilter) {
        const coach = this.coachOptions.find((c) => String(c.id) === String(this.assignedFilter));
        labels.push(coach ? coach.full_name : 'a coach');
      }
      return labels;
    },
    // Approximate count of leads hidden by the current filters in the active
    // view. Uses summary counts as the denominator so it's free (no extra
    // request). Slightly over-counts in mixed scenarios (e.g. assignedFilter
    // = "me" + stage filter) but the order of magnitude is right and the
    // banner's job is "filters are doing something."
    hiddenLeadsCount() {
      if (!this.summaryLoaded) return 0;
      const total = this.viewMode === 'queue'
        ? (this.summary.families_needs_attention_count || 0)
        : (this.summary.families_total_count || 0);
      return Math.max(0, total - this.families.length);
    },
    // True when the rendered list (queue or all-leads) has nothing to show.
    // Used to surface the filter banner even on the default 'me' filter when
    // the coach is staring at an empty page — the most common "system feels
    // broken" moment is a new unassigned inquiry hidden by My leads.
    visibleListEmpty() {
      if (this.viewMode === 'queue') {
        return this.families.length === 0 && this.upcomingTrials.length === 0;
      }
      return this.families.length === 0;
    },
    showFilterBanner() {
      if (this.hiddenLeadsCount === 0) return false;
      if (this.hasActiveFilters) return true;
      // Default 'me' alone normally suppresses the banner, but if the page
      // is empty AND leads are hidden, the coach needs to know why.
      return this.visibleListEmpty && this.assignedFilter === 'me';
    },
    filterableQuestions() {
      const filterableTypes = ['RegSingleSelectQuestion', 'RegMultiSelectQuestion', 'RegYesNoQuestion'];
      return this.prospectQuestions.filter((q) => filterableTypes.includes(q.type));
    },
    filterAnswerOptions() {
      if (!this.filterQuestionId) return [];
      const question = this.prospectQuestions.find((q) => q.id === parseInt(this.filterQuestionId, 10));
      if (!question || !question.answers) return [];
      if (question.type === 'RegYesNoQuestion') {
        return question.answers.map((a) => a.prompt);
      }
      return question.answers;
    },
    selectedFamily() {
      if (!this.selectedFamilyId) return null;
      return (
        this.families.find((f) => f.id === this.selectedFamilyId)
        || this.upcomingTrials.find((f) => f.id === this.selectedFamilyId)
        || (this.deepLinkedFamily && this.deepLinkedFamily.id === this.selectedFamilyId
          ? this.deepLinkedFamily
          : null)
      );
    },
  },
  created() {
    // Email CTA lands the coach on /prospects?family_id=123 — capture it
    // before any filter changes so the drawer can open after the first
    // families fetch resolves. Read once on mount and forget.
    const params = new URLSearchParams(window.location.search);
    const requested = parseInt(params.get('family_id'), 10);
    this.pendingDeepLinkFamilyId = Number.isInteger(requested) ? requested : null;

    this.loadSummary();
    this.loadFamilies();
    if (this.viewMode === 'queue') this.loadUpcomingTrials();
    this.loadProspectQuestions();
  },
  methods: {
    // Builds a starting URL for $apiService.loadAllPages, which follows the
    // Link rel="next" chain. We use a generous per_page to minimize round-trips
    // but the helper handles pagination either way.
    buildUrl(opts = {}) {
      const url = this.familiesApiUrl;
      const params = ['per_page=100'];
      if (this.searchActive && !opts.ignoreSearch) {
        params.push(`query=${encodeURIComponent(this.searchQuery.trim())}`);
      } else {
        // Queue narrows to needs-attention; All Leads sees everything.
        if (this.viewMode === 'queue') {
          params.push(`attention_mode=${this.attentionMode}`);
        }
        if (this.activeStage) params.push(`stage=${encodeURIComponent(this.activeStage)}`);
        if (this.assignedFilter === 'me') params.push('assigned_to=me');
        else if (this.assignedFilter) params.push(`assigned_coach_id=${encodeURIComponent(this.assignedFilter)}`);
        if (this.filterQuestionId && this.filterAnswerValue) {
          params.push(`question_id=${encodeURIComponent(this.filterQuestionId)}`);
          params.push(`answer_value=${encodeURIComponent(this.filterAnswerValue)}`);
        }
      }
      const separator = url.indexOf('?') !== -1 ? '&' : '?';
      return url + separator + params.join('&');
    },

    loadProspectQuestions() {
      if (!this.questionsUrl) return;
      axios.get(this.questionsUrl)
        .then((response) => {
          this.prospectQuestions = response.data.registration_questions || [];
        })
        .catch(() => {});
    },

    loadSummary() {
      const separator = this.summaryUrl.indexOf('?') !== -1 ? '&' : '?';
      let url;
      if (this.conversionRange === 'custom' && this.conversionStartDate && this.conversionEndDate) {
        url = `${this.summaryUrl + separator}start_date=${this.conversionStartDate}&end_date=${this.conversionEndDate}`;
      } else {
        const days = this.conversionRange === 'custom' ? 90 : this.conversionRange;
        url = `${this.summaryUrl + separator}conversion_days=${days}`;
      }
      axios.get(url)
        .then((response) => {
          this.summary = response.data;
          this.summaryLoaded = true;
        })
        .catch(() => {});
    },

    onConversionRangeChange({ range, start, end }) {
      this.conversionRange = range;
      if (range === 'custom') {
        this.conversionStartDate = start;
        this.conversionEndDate = end;
      }
      this.loadSummary();
    },

    loadFamilies() {
      this.loading = true;
      this.error = false;
      this.families = [];

      this.$apiService.loadAllPages(this.buildUrl(), 'prospect_families')
        .then((all) => {
          this.loading = false;
          this.families = all;
          this.maybeOpenDeepLinkedFamily();
        })
        .catch(() => {
          this.loading = false;
          this.error = true;
          this.errorMessage = 'Error loading leads. Please try refreshing the page.';
        });
    },

    // Drawer deep-link from the new-inquiry email. Runs once after the first
    // families fetch. If the requested family is in the current list we just
    // open the drawer; otherwise we fetch it directly so the user's filters
    // (e.g. "My leads") stay put and the drawer still opens.
    maybeOpenDeepLinkedFamily() {
      const id = this.pendingDeepLinkFamilyId;
      if (!id) return;
      this.pendingDeepLinkFamilyId = null;

      const inList = this.families.find((f) => f.id === id);
      if (inList) {
        this.selectedFamilyId = id;
        return;
      }

      const base = this.familiesApiUrl.split('?')[0];
      axios.get(`${base}/${id}`)
        .then((response) => {
          const family = response.data;
          if (family && family.id) {
            this.deepLinkedFamily = family;
            this.selectedFamilyId = family.id;
          }
        })
        .catch(() => {});
    },

    loadUpcomingTrials() {
      // Pull every trial_scheduled family so the 48h client-filter is accurate.
      // Ignores coach filter so upcoming trials show regardless of who's assigned.
      const sep = this.familiesApiUrl.indexOf('?') !== -1 ? '&' : '?';
      const url = `${this.familiesApiUrl}${sep}stage=trial_scheduled&per_page=100&attention_mode=`;
      this.$apiService.loadAllPages(url, 'prospect_families')
        .then((all) => {
          const now = new Date();
          const horizon = new Date(now.getTime() + 48 * 3600 * 1000);
          this.upcomingTrials = all.filter((family) => (
            family.prospects.some((c) => {
              if (c.stage !== 'trial_scheduled' || !c.trial_scheduled_at) return false;
              const t = new Date(c.trial_scheduled_at);
              return t >= now && t <= horizon;
            })
          ));
        })
        .catch(() => {});
    },

    onViewModeChange(mode) {
      if (mode === this.viewMode) {
        // Clicking the active tab is the user's "give me everything back"
        // gesture (per coach UX testing). Run the full reset whenever
        // there's anything narrower than "all stages + all coaches" set.
        if (this.hasActiveFilters || this.assignedFilter !== '') {
          this.resetFilters();
        }
        return;
      }
      this.viewMode = mode;
      localStorage.setItem('prospect_list_view_mode', mode);
      // Clear stage filter on mode switch — what's relevant differs per view.
      this.activeStage = '';
      // Funnel report is a self-contained aggregate view that doesn't read
      // the families list — skip the network round-trip.
      if (mode !== 'funnel') this.loadFamilies();
      if (mode === 'queue') this.loadUpcomingTrials();
    },

    // Single reset path used by both the banner button and the active-tab
    // click. Wipes every narrowing filter including the coach perspective,
    // landing the coach on "all stages, all leads."
    resetFilters() {
      this.searchQuery = '';
      this.activeStage = '';
      this.filterQuestionId = '';
      this.filterAnswerValue = '';
      this.assignedFilter = '';
      localStorage.setItem('prospect_list_assigned_filter', '');
      clearTimeout(this.searchDebounceTimer);
      this.loadFamilies();
    },

    stageLabel(stage) {
      const labels = {
        inquiry: 'Interested',
        trial_scheduled: 'Trial Scheduled',
        trialing: 'Trialing',
        trial_complete: 'Trial Complete',
        converted: 'Converted',
        didnt_join: "Didn't Join",
        archived: 'Archived',
      };
      return labels[stage] || stage;
    },

    onSearchInput(value) {
      this.searchQuery = value;
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        this.loadFamilies();
      }, 300);
    },

    onStageChange(stage) {
      this.activeStage = stage;
      this.loadFamilies();
    },

    onAssignedFilterChange(val) {
      this.assignedFilter = val;
      localStorage.setItem('prospect_list_assigned_filter', val);
      this.loadFamilies();
    },

    onFilterQuestionChange(val) {
      this.filterQuestionId = val;
      this.filterAnswerValue = '';
    },

    onFilterAnswerChange(val) {
      this.filterAnswerValue = val;
      if (val) this.loadFamilies();
    },

    clearAnswerFilter() {
      this.filterQuestionId = '';
      this.filterAnswerValue = '';
      this.loadFamilies();
    },

    openDrawer(family) {
      this.selectedFamilyId = family.id;
    },

    closeDrawer() {
      this.selectedFamilyId = null;
    },

    onStageChangePayload({ child, newStage, extra }) {
      this.performStageChange(child, newStage, extra);
    },

    onTrialDateSave({ child, iso }) {
      this.patchChild(child, { trial_scheduled_at: iso })
        .then(() => this.$notificationManager.emit('show-toast', 'Trial date updated', true))
        .catch(() => this.$notificationManager.emit('show-toast', 'Error updating trial date', false));
    },

    // The drawer hands us the prospect the booking POST returned, so this is a
    // local repaint — no PATCH, no refetch of the child. The family-level GET
    // still runs because booking can move the lead's stage counts.
    onTrialClassBooked({ child, prospect }) {
      // The drawer closes the picker before this runs, so a malformed response
      // would otherwise leave the coach with no confirmation either way.
      if (!prospect) {
        this.$notificationManager.emit('show-toast', 'Error booking trial class', false);
        return;
      }

      const family = this.findFamilyForChild(child);
      if (family) {
        this.updateChildInFamily(family, child, prospect);
        axios.get(`${this.familiesApiUrl}/${family.id}`)
          .then((fresh) => this.mergeFamilyTopLevel(family, fresh.data))
          .catch(() => {});
      }
      this.$notificationManager.emit('show-toast', 'Trial class booked', true);
      this.loadSummary();
    },

    onFollowUpToggle({ child, needs_follow_up: nextFlag }) {
      this.patchChild(child, { needs_follow_up: nextFlag })
        .then(() => {
          const msg = nextFlag ? 'Marked for follow-up' : 'Follow-up cleared';
          this.$notificationManager.emit('show-toast', msg, true);
          this.loadSummary();
        })
        .catch(() => this.$notificationManager.emit('show-toast', 'Error updating follow-up', false));
    },

    onFamilyContactSave({ family, attrs }) {
      axios.patch(`${this.familiesApiUrl}/${family.id}`, { prospect_family: attrs })
        .then((response) => {
          this.replaceFamily(family, response.data);
          this.$notificationManager.emit('show-toast', 'Contact updated', true);
        })
        .catch((error) => {
          this.$notificationManager.emit('show-toast', this.firstApiError(error, 'Error updating contact'), false);
        });
    },

    onChildDetailsSave({ child, attrs }) {
      this.patchChild(child, attrs)
        .then(() => this.$notificationManager.emit('show-toast', 'Wrestler updated', true))
        .catch((error) => {
          this.$notificationManager.emit('show-toast', this.firstApiError(error, 'Error updating wrestler'), false);
        });
    },

    // Pulls a human-readable message out of either an array-style or
    // hash-style errors payload (controllers use validation_error which
    // serializes ActiveModel::Errors as {field: [messages]}).
    firstApiError(error, fallback) {
      const errors = error && error.response && error.response.data && error.response.data.errors;
      if (!errors) return fallback;
      if (Array.isArray(errors)) return errors.length ? errors.join(', ') : fallback;
      const keys = Object.keys(errors);
      if (keys.length === 0) return fallback;
      const first = errors[keys[0]];
      const msg = Array.isArray(first) ? first[0] : first;
      const label = keys[0].replace(/_/g, ' ');
      return msg ? `${label} ${msg}` : fallback;
    },

    onAssignedCoachChange(coachId) {
      const family = this.selectedFamily;
      if (!family) return;
      const currentId = family.assigned_coach ? family.assigned_coach.id : null;
      const newId = coachId ? parseInt(coachId, 10) : null;
      if (currentId === newId) return;

      const params = { prospect_family: { assigned_coach_id: newId } };
      axios.patch(`${this.familiesApiUrl}/${family.id}`, params)
        .then((response) => {
          this.replaceFamily(family, response.data);
          const message = newId ? 'Coach assigned' : 'Unassigned';
          this.$notificationManager.emit('show-toast', message, true);
        })
        .catch(() => {
          this.$notificationManager.emit('show-toast', 'Error updating assignment', false);
        });
    },

    onNoteCreated(family) {
      this.refreshFamily(family);
    },

    onDrawerNoteCreated() {
      if (this.selectedFamily) this.refreshFamily(this.selectedFamily);
    },

    refreshFamily(family) {
      axios.get(`${this.familiesApiUrl}/${family.id}`)
        .then((response) => {
          this.replaceFamily(family, response.data);
          this.loadSummary();
        })
        .catch(() => {});
    },

    performStageChange(child, newStage, extraParams) {
      const family = this.findFamilyForChild(child);
      if (!family) return;
      const attrs = { stage: newStage, ...extraParams || {} };
      this.patchChild(child, attrs)
        .then(() => {
          this.$notificationManager.emit('show-toast', 'Stage updated', true);
          this.loadSummary();
          // If a stage filter is active and the new stage no longer matches,
          // the in-place row would linger. Refetch so the filtered view stays
          // honest.
          if (this.activeStage && this.activeStage !== newStage) {
            this.loadFamilies();
          }
          this.loadUpcomingTrials();
        })
        .catch(() => {
          this.$notificationManager.emit('show-toast', 'Error updating stage', false);
        });
    },

    patchChild(child, attrs) {
      return axios.patch(`${this.apiUrl}/${child.id}`, { prospect: attrs })
        .then((response) => {
          const family = this.findFamilyForChild(child);
          if (family) this.updateChildInFamily(family, child, response.data);
          if (family) {
            return axios.get(`${this.familiesApiUrl}/${family.id}`)
              .then((fresh) => this.mergeFamilyTopLevel(family, fresh.data))
              .catch(() => {});
          }
          return null;
        })
        .catch((error) => {
          // Callers chain their own .catch() to toast the failure, so the
          // rejection has to keep propagating or a failed PATCH shows a
          // success toast.
          throw error;
        });
    },

    findFamilyForChild(child) {
      const inDeepLink = this.deepLinkedFamily
        && this.deepLinkedFamily.prospects
        && this.deepLinkedFamily.prospects.some((p) => p.id === child.id);
      return this.families.find((f) => f.prospects.some((p) => p.id === child.id))
        || this.upcomingTrials.find((f) => f.prospects.some((p) => p.id === child.id))
        || (inDeepLink ? this.deepLinkedFamily : null);
    },

    replaceFamily(family, data) {
      const updateArray = (arr) => {
        const idx = arr.findIndex((f) => f.id === family.id);
        if (idx !== -1) this.$set(arr, idx, data);
      };
      updateArray(this.families);
      updateArray(this.upcomingTrials);
      // The drawer can be opened via ?family_id= for a family that isn't in
      // the current filtered list — keep that source of truth in sync too.
      if (this.deepLinkedFamily && this.deepLinkedFamily.id === family.id) {
        this.deepLinkedFamily = data;
      }
    },

    mergeFamilyTopLevel(family, data) {
      const fields = [
        'contact_name', 'contact_email', 'contact_phone', 'hear_about_us',
        'source', 'needs_attention', 'guardian', 'assigned_coach',
        'registration_answers', 'updated_at',
        'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
      ];
      fields.forEach((field) => {
        if (data[field] !== undefined) this.$set(family, field, data[field]);
      });
    },

    updateChildInFamily(family, child, data) {
      const idx = family.prospects.findIndex((p) => p.id === child.id);
      if (idx === -1) return;
      this.$set(family.prospects, idx, data);
    },
  },
};
</script>
